import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
} from '@mui/material';
import { FC } from 'react';
import { useRoutesAndScheduleState } from '../../../contexts/routesAndSchedule.context';
import { useTranslation } from 'src/hooks/useTranslationHook';

const SelectedSalaryDetailsView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedDriver, vehicles, routes } = useRoutesAndScheduleState();

  if (!selectedDriver) {
    return (
      <Box
        sx={{ width: '100%', height: '100%', overflow: 'auto', p: 3 }}
        data-testid="salary-details-container"
      >
        <Typography>
          {translateText('administration.routesAndSchedule.noData')}
        </Typography>
      </Box>
    );
  }

  const assignedVehicle = vehicles.find(
    (v) => v.id === selectedDriver.assignedVehicleId
  );
  const assignedRoute = routes.find(
    (r) => r.id === selectedDriver.assignedRouteId
  );

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return translateText('common.nA');
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString();
  };

  const getSalaryStatusColor = (status: string) => {
    switch (status) {
      case 'PAID':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'OVERDUE':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box
      sx={{ width: '100%', height: '100%', overflow: 'auto', p: 3 }}
      data-testid="salary-details-container"
    >
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        {translateText('administration.routesAndSchedule.columns.driverName')}:{' '}
        {selectedDriver.name}
      </Typography>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {translateText(
              'administration.routesAndSchedule.details.basicInfo'
            )}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} data-testid="driver-name">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.driverName'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedDriver.name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="driver-license">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.licenseNumber'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedDriver.licenseNumber}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="driver-license-type">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.details.licenseType'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedDriver.licenseType}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="driver-phone">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.phoneNumber'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedDriver.phoneNumber}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="driver-email">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.details.email'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedDriver.email || translateText('common.nA')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="driver-experience">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.experience'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedDriver.experience}{' '}
                {translateText(
                  'administration.routesAndSchedule.details.years'
                )}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="driver-status">
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block' }}
              >
                {translateText(
                  'administration.routesAndSchedule.columns.status'
                )}
              </Typography>
              <Chip
                label={
                  selectedDriver.isActive
                    ? translateText(
                        'administration.routesAndSchedule.status.active'
                      )
                    : translateText(
                        'administration.routesAndSchedule.status.inactive'
                      )
                }
                color={selectedDriver.isActive ? 'success' : 'default'}
                size="small"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {translateText('administration.routesAndSchedule.details.salary')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} data-testid="driver-salary">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.salary'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                ₹{selectedDriver.salary.toLocaleString()}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="driver-salary-status">
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block' }}
              >
                {translateText(
                  'administration.routesAndSchedule.columns.salaryStatus'
                )}
              </Typography>
              <Chip
                label={translateText(
                  `administration.routesAndSchedule.status.${selectedDriver.salaryStatus.toLowerCase()}`
                )}
                color={getSalaryStatusColor(selectedDriver.salaryStatus) as any}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="driver-last-paid">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.lastPaidDate'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDate(selectedDriver.lastSalaryPaidDate)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="driver-next-due">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.nextDueDate'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDate(selectedDriver.nextSalaryDueDate)}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {translateText(
              'administration.routesAndSchedule.details.assignments'
            )}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} data-testid="driver-vehicle">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.vehicle'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {assignedVehicle?.vehicleNumber ||
                  translateText(
                    'administration.routesAndSchedule.status.unassigned'
                  )}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} data-testid="driver-route">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.route'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {assignedRoute?.routeName ||
                  translateText(
                    'administration.routesAndSchedule.status.unassigned'
                  )}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {translateText('administration.routesAndSchedule.details.contact')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} data-testid="driver-address">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.details.address'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedDriver.address}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} data-testid="driver-emergency-contact">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.details.emergencyContact'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedDriver.emergencyContact}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} data-testid="driver-emergency-phone">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.details.emergencyPhone'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedDriver.emergencyContactPhone}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {translateText(
              'administration.routesAndSchedule.details.certifications'
            )}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} data-testid="driver-license-expiry">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.details.licenseExpiry'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDate(selectedDriver.licenseExpiryDate)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} data-testid="driver-medical-expiry">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.details.medicalExpiry'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDate(selectedDriver.medicalCertificateExpiryDate)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} data-testid="driver-police-verification">
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block' }}
              >
                {translateText(
                  'administration.routesAndSchedule.details.policeVerification'
                )}
              </Typography>
              <Chip
                label={selectedDriver.policeVerificationStatus}
                color={
                  selectedDriver.policeVerificationStatus === 'VERIFIED'
                    ? 'success'
                    : 'warning'
                }
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} data-testid="driver-joining-date">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.details.joiningDate'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDate(selectedDriver.joiningDate)}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SelectedSalaryDetailsView;
